<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";

import KSASecondaryButton from "../../../../../components/ui-components/button/KSASecondaryButton.vue";
import KSAPrimaryButton from "../../../../../components/ui-components/button/KSAPrimaryButton.vue";
import KSATextButton from "../../../../../components/ui-components/button/KSATextButton.vue";
import adminJobfairStateService from "../../../services/jobfair/adminJobfairStateService";
import JobFairJobNoticeDetailModal from "../../../../user/components/modal/jobFair/JobFairJobNoticeDetailModal.vue";
import mypageJobFairJobNoticeService from "../../../../user/services/mypage/jobFair/mypageJobFairJobNoticeService";
import { useAlertStore } from "../../../../../stores/alertStore";
import { usePageLoadingStore } from "../../../../../stores/pageLoading";
import moment from "moment";
import JobFairCorpPostingsTabApplicantListView from "./JobFairCorpPostingsTabApplicantListView.vue";

const { $alert, $confirm } = useAlertStore();
const { $pageListBoardNo, $toast, $number, $gbNameKR, $yyyymmdd, $yyyymmddHHmmss, $interests, $jobNoticeStateGb, $intvGb } = getCurrentInstance().appContext.config.globalProperties;

const route = useRoute();
const router = useRouter();
const loadingStore = usePageLoadingStore();

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const totalCount = ref(0);
const searchOpen = ref(true);
const list = ref([]);

const isApplicantView = ref(false);
const selectedJobNoticeSeq = ref('');
const selectedCorpId = ref('');

// 모달
const jobNoticeModal = ref(null);
// 검색
const searchTypeRef = ref(null);
const interestsListRef = ref(null);
const jobNoticeStateGbsListRef = ref(null);
const intvsListRef = ref(null);

const data_interests = ref($interests); // 관심 카테고리
const data_jobNoticeStateGb = ref($jobNoticeStateGb) // 채용 상태
const data_intvGb = ref($intvGb); // 면접방식

const pageSizeGroup = ref([
  { label:"10개씩 보기", value: 10 },
  { label:"30개씩 보기", value: 30 },
  { label:"50개씩 보기", value: 50 },
  { label:"100개씩 보기", value: 100 },
])

const searchDurationTypeGroup = ref([
  { label:'모든 기간', value: 'all' },
  { label:'기간 검색', value: 'term' },
])

const searchInput = ref({ // 재직자/기업(1) - 채용공고(2)
  searchInterests: ['설계','장비','소재','제조공정','패키지','테스트','SW','기타'],
  searchJobNoticeStateGbs: ['01', '02', '03'],
  searchIntvGbs: ['01', '02', '03', '04'],
  searchDurationType: 'all',
  startYmd: '',
  endYmd: '',
  searchType: 'corpName', // corpName 기업명, title 채용공고제목, name 등록마스터
  searchInput: '',
});

const searchTypeGroup = ref([
  { label:"기업명", value: "corpName", placeholder: "기업명을 입력해주세요."  },
  { label:"채용공고 제목", value: "title", placeholder: "채용공고 제목을 입력해주세요." },
  { label:'등록 마스터', value: 'name', placeholder: "등록 마스터 이름을 입력해주세요." },
]);

const searchInputPlaceholder = computed(()=>{
  const option = searchTypeGroup.value.find((item) => item.value === searchInput.value.searchType);
  if(option) {
    return option.placeholder;
  } else {
    return '';
  }
})

const onSelectSearchTypeGroup = (option) => {
  searchInput.value.searchType = option.value;
}

const onSelectPage = (pageNo) => {
  currentPage.value = pageNo;
  retrieveList();
}

// 페이지당 개수 변경
const onSelectPageSize = (option) => {
  pageSize.value = option.value;
  currentPage.value = 1;
  retrieveList();
}

const onClickSearch = () => {
  currentPage.value = 1;
  retrieveList();
}

const clearSearchInput = () => {
  searchInput.value = {
    searchInterests: ['설계','장비','소재','제조공정','패키지','테스트','SW','기타'],
    searchJobNoticeStateGbs: ['01', '02', '03'],
    searchIntvGbs: ['01', '02', '03', '04'],
    searchDurationType: 'all',
    startYmd: '',
    endYmd: '',
    searchType: 'corpName',
    searchInput: '',
  }
  searchTypeRef.value?.initSelectedOption(searchInput.value.searchType);
  interestsListRef.value?.initOptions();
  jobNoticeStateGbsListRef.value?.initOptions();
  intvsListRef.value?.initOptions();
}

const retrieveList = () => {
  const now = new Date();
  const startYmd = searchInput.value.startYmd ? searchInput.value.startYmd : moment(now).format('YYYYMMDD');
  const endYmd = searchInput.value.endYmd ? searchInput.value.endYmd : moment(new Date(now.setDate(now.getDate() + 30))).format('YYYYMMDD');

  let paramObj = {
    searchInterests: searchInput.value.searchInterests.join(','),
    searchJobNoticeStateGbs: searchInput.value.searchJobNoticeStateGbs.join(','),
    searchIntvGbs: searchInput.value.searchIntvGbs.join(','),
    startYmd: searchInput.value.searchDurationType === 'all' ? '' : startYmd,
    endYmd: searchInput.value.searchDurationType === 'all' ? '' : endYmd,
    searchType: searchInput.value.searchType,
    searchInput: searchInput.value.searchInput,
    pageNumber: currentPage.value,
    size: pageSize.value,
  }
  loadingStore.loading = true;
  adminJobfairStateService.retrieveJobNoticeListPaging(paramObj)
    .then( async (res) => {
      loadingStore.loading = false;
      if (res.data.code.toLowerCase() === "success") {
        const result = res.data.result;
        totalPages.value = result.totalPages;
        totalCount.value = result.totalElements;
        list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
      }
    }).catch((res) => {
    loadingStore.loading = false;
    $toast("error", res);
  });
};

// 입사지원 수 클릭시
const onClickApplicantList = (jobNoticeSeq, corpId) => {
  isApplicantView.value = true;
  selectedJobNoticeSeq.value = jobNoticeSeq;
  selectedCorpId.value = corpId;
}

// 채용공고 보기
const onClickJobNotice = async (jobNoticeSeq, corpId) => {
  await jobNoticeModal.value.show({
    jobNoticeSeq,
    corpId,
  });
}

// 채용공고 수정
const onClickEditJobNotice = (jobNoticeSeq) => {
  router.push({ name: 'MyPageCorpJobNotice', query : { jobNoticeSeq, mode: 'admin' }});
};

// 채용공고 삭제
const onClickDeleteJobNotice = async (jobNoticeSeq) => {
  const ok = await $confirm(["채용공고를 삭제하시겠습니까?"], "삭제");
  if(ok) {
    mypageJobFairJobNoticeService.deleteJobNotice({ jobNoticeSeq })
      .then( async (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          $alert("채용공고가 삭제되었습니다.");
          retrieveList();
        } else {
          if (res.response.data.result.message) {
            $alert([res.response.data.result.message]);
          } else {
            $alert("채용공고 삭제를 실패하였습니다.");
          }
        }
      })
      .catch( (res) =>  {
        $alert([res.response.data.result.message]);
        loadingStore.loading = false;
      });
  }
};

const openDetailInNewTab = (linkName, id, query = {}) => {
  const url = router.resolve({
    name: linkName,
    params: { id },
    query,
  }).href;

  window.open(url, '_blank');
}

onMounted(()=>{
  retrieveList();
});
</script>

<template>
  <div v-show="!isApplicantView">
    <!-- s: 검색 -->
    <div class="filter-wrap" :class="{'open': searchOpen}">
      <p @click="searchOpen = !searchOpen">검색</p>
      <div class="filter">
        <dl>
          <dt style="display:block;"><span>관심 카테고리</span><br/><small style="font-weight: normal;">복수 선택 가능</small></dt>
          <dd>
            <KSACheckBoxGroup
              ref="interestsListRef"
              className="mr-md"
              name="interestsList"
              v-model="searchInput.searchInterests"
              :options="data_interests"
            />
          </dd>
        </dl>
        <dl>
          <dt>채용 상태</dt>
          <dd>
            <KSACheckBoxGroup
              ref="jobNoticeStateGbsListRef"
              className="mr-md"
              name="jobNoticeStateGbsList"
              v-model="searchInput.searchJobNoticeStateGbs"
              :options="data_jobNoticeStateGb"
            />
          </dd>
        </dl>
        <dl>
          <dt>면접 방식</dt>
          <dd>
            <KSACheckBoxGroup
              ref="intvsListRef"
              className="mr-md"
              name="intvsList"
              v-model="searchInput.searchIntvGbs"
              :options="data_intvGb"
            />
          </dd>
        </dl>
        <dl>
          <dt>등록일</dt>
          <dd class="text-box">
            <div class="grid">
              <template v-for="group in searchDurationTypeGroup" key="item.value" >
                <KSARadioButton class="mr-md" :label="group.label" v-model="searchInput.searchDurationType" :value="group.value" />
              </template>
              <div style="display: inline-block;" v-if="searchInput.searchDurationType !== 'all'">
                <KSADatepickerTerms
                  :startDate="searchInput.startYmd"
                  :endDate="searchInput.endYmd"
                  @update:startDate="(value)=> searchInput.startYmd = value"
                  @update:endDate="(value)=> searchInput.endYmd = value"
                />
              </div>
            </div>
          </dd>
        </dl>
        <!-- corpName 기업명, title 채용공고제목, name 등록마스터 검색 -->
        <dl>
          <dt>
            <KSASelect ref="searchTypeRef" :options="searchTypeGroup" class="select" @selectOption="onSelectSearchTypeGroup"/>
          </dt>
          <dd>
            <KSAInput class="text-box" v-model="searchInput.searchInput" :placeholder="searchInputPlaceholder" />
          </dd>
        </dl>
        <div class="btn-wrap">
          <KSASecondaryButton class="gray_button" text="초기화" @click="clearSearchInput" />
          <KSAPrimaryButton text="검색" @click="onClickSearch" />
        </div>
      </div>
    </div>
    <!-- e: 검색 -->
    <!-- s: 버튼영역 -->
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>개</p>
      <div>
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize" />
      </div>
    </div>
    <!-- e: 버튼영역 -->
    <div class="table-form">
      <table class="horizontal" v-if="list !== null">
        <tr>
          <th>NO</th>
          <th>채용상태</th>
          <th>기업명</th>
          <th>채용공고 제목</th>
          <th>마스터</th>
          <th>관심 카테고리</th>
          <th>면접 방식</th>
          <th>채용기간</th>
          <th>입사지원</th>
          <th>최근 수정일</th>
          <th>등록일</th>
          <th>관리</th>
        </tr>
        <tr v-for="corp in list">
          <td>{{ corp.boardNo }}</td>
          <td>{{ $gbNameKR("JOB_NOTICE_STATE_GB", corp.jobNoticeStateGb) }}</td>
          <td class="clickable blue text-overflow long-overflow">
            <span @click="openDetailInNewTab('CorporationDetail', corp.corpId, { tab: 3, tab2: 'job-postings' })">{{ corp.corpName }}</span>
          </td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(corp.jobNoticeSeq, corp.corpId)">{{ corp.jobName }}</td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', corp.createId)" >{{ corp.userName }}</span>
          </td>
          <td>{{ corp.interests }}</td>
          <td>{{ $gbNameKR("INTV_GB", corp.intvGb) }}</td>
          <td>{{ $yyyymmdd(corp.recruitStartYmd) }} ~ {{ $yyyymmdd(corp.recruitEndYmd) }}</td>
          <td class="clickable blue" @click="onClickApplicantList(corp.jobNoticeSeq, corp.corpId, corp.fileYn)">{{ corp.applicantCnt }}</td>
          <td>{{ $yyyymmddHHmmss(corp.modifyDate) }}</td>
          <td>{{ $yyyymmddHHmmss(corp.createDate) }}</td>
          <td>
            <KSATextButton text="수정" @click="onClickEditJobNotice(corp.jobNoticeSeq)" class="clickable"/>&nbsp;
            <KSATextButton v-if="corp.applicantCnt === 0" text="삭제" @click="onClickDeleteJobNotice(corp.jobNoticeSeq)" class="clickable red"/>
          </td>
        </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
        등록된 채용공고가 없습니다.
      </div>
    </div>
    <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectPage"
    />
  </div>
  <div v-show="isApplicantView">
    <JobFairCorpPostingsTabApplicantListView
      :job-notice-seq="selectedJobNoticeSeq"
      :corp-id="selectedCorpId"
      @go-back="isApplicantView = false;"
    />
  </div>
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
</template>

<style scoped>
</style>
